import { useCallback, useEffect, useRef, useState } from "react";
import { StickyNote, X, Plus, Check, Trash2 } from "lucide-react";
import api from "@/api/client";
import { toast } from "sonner";

/**
 * Floating "Personal Notes + To-do" widget.
 * A fixed circular button sits above the chat FAB. Clicking it opens a compact
 * panel with two panes: a free-form notepad and a checklist. Everything is
 * private to the caller — the API scopes both collections by `user_id`.
 */
export default function PersonalNotesWidget() {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState("notes"); // notes | todos
  const [note, setNote] = useState("");
  const [todos, setTodos] = useState([]);
  const [newTodo, setNewTodo] = useState("");
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const noteRef = useRef(note);

  const loadAll = useCallback(async () => {
    try {
      const [n, t] = await Promise.all([api.get("/notes"), api.get("/todos")]);
      setNote(n.data?.text || "");
      noteRef.current = n.data?.text || "";
      setTodos(t.data || []);
    } catch (_e) { /* silent on first load; user can retry via reopen */ }
    finally { setLoaded(true); }
  }, []);

  useEffect(() => { if (open && !loaded) loadAll(); }, [open, loaded, loadAll]);

  // Debounced save for the notepad
  useEffect(() => {
    if (!open || !loaded) return;
    if (note === noteRef.current) return;
    const id = setTimeout(async () => {
      try {
        setSaving(true);
        await api.put("/notes", { text: note });
        noteRef.current = note;
      } catch {
        toast.error("Note not saved");
      } finally { setSaving(false); }
    }, 600);
    return () => clearTimeout(id);
  }, [note, open, loaded]);

  const addTodo = async () => {
    if (!newTodo.trim()) return;
    try {
      const { data } = await api.post("/todos", { text: newTodo.trim() });
      setTodos((prev) => [...prev, data]);
      setNewTodo("");
    } catch {
      toast.error("Couldn't add to-do");
    }
  };

  const toggleTodo = async (t) => {
    try {
      const { data } = await api.patch(`/todos/${t.id}`, { done: !t.done });
      setTodos((prev) => prev.map((x) => (x.id === t.id ? data : x)));
    } catch {
      toast.error("Update failed");
    }
  };

  const removeTodo = async (t) => {
    try {
      await api.delete(`/todos/${t.id}`);
      setTodos((prev) => prev.filter((x) => x.id !== t.id));
    } catch {
      toast.error("Delete failed");
    }
  };

  const pending = todos.filter((t) => !t.done).length;

  return (
    <>
      {/* FAB — sits ~140px above bottom to clear the chat FAB and bottom nav */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="notes-fab"
        aria-label="Personal notes & to-do"
        className={`fixed right-6 z-40 h-11 w-11 rounded-full flex items-center justify-center border shadow-lg transition-transform ${
          open
            ? "bg-white text-black border-white/40 rotate-90"
            : "bg-surface text-white border-white/10 hover:scale-105"
        } bottom-[9rem] lg:bottom-[8.5rem]`}
      >
        {open ? <X size={16} /> : <StickyNote size={16} />}
        {!open && pending > 0 && (
          <span className="absolute -top-1 -right-1 text-[0.65rem] font-mono bg-[#E11D2E] text-white rounded-full h-4 min-w-4 px-1 flex items-center justify-center">
            {pending}
          </span>
        )}
      </button>

      {/* Panel */}
      {open && (
        <div
          data-testid="notes-panel"
          className="fixed right-6 bottom-[13rem] lg:bottom-[12.5rem] z-40 w-[92vw] sm:w-[380px] max-h-[70vh] rounded-xl border border-white/10 bg-surface shadow-2xl shadow-black/60 flex flex-col overflow-hidden"
        >
          <div className="px-4 py-3 border-b border-white/5 flex items-center justify-between">
            <div>
              <div className="overline">Private Workspace</div>
              <div className="text-sm font-display">Notes & To-do</div>
            </div>
            <div className="inline-flex rounded-full border border-white/10 p-0.5 bg-black/40">
              <button
                onClick={() => setTab("notes")}
                data-testid="notes-tab-notes"
                className={`text-[0.7rem] uppercase tracking-widest px-2.5 py-1 rounded-full ${
                  tab === "notes" ? "bg-white text-black" : "text-white/60"
                }`}
              >Notes</button>
              <button
                onClick={() => setTab("todos")}
                data-testid="notes-tab-todos"
                className={`text-[0.7rem] uppercase tracking-widest px-2.5 py-1 rounded-full ${
                  tab === "todos" ? "bg-white text-black" : "text-white/60"
                }`}
              >To-do {pending > 0 && <span className="ml-1 text-[0.65rem] font-mono">{pending}</span>}</button>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-4">
            {tab === "notes" ? (
              <div>
                <textarea
                  data-testid="notes-textarea"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="Jot down anything — client contacts, ideas, follow-ups. Only you can see this."
                  className="w-full min-h-[240px] bg-black/40 border border-white/10 rounded-md p-3 text-sm text-white/85 focus:outline-none focus:ring-2 focus:ring-red-500 leading-relaxed"
                />
                <div className="text-[0.7rem] text-white/40 mt-2 font-mono">
                  {saving ? "Saving…" : "Autosaved · private to you"}
                </div>
              </div>
            ) : (
              <div>
                <div className="flex gap-2 mb-3">
                  <input
                    data-testid="todos-input"
                    value={newTodo}
                    onChange={(e) => setNewTodo(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addTodo(); } }}
                    placeholder="Add a to-do…"
                    className="flex-1 bg-black/40 border border-white/10 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
                  />
                  <button
                    onClick={addTodo}
                    data-testid="todos-add-btn"
                    className="bg-[#E11D2E] hover:bg-red-700 text-white rounded-md px-3 flex items-center gap-1"
                  ><Plus size={14} /></button>
                </div>
                {todos.length === 0 ? (
                  <div className="text-[12px] text-white/40 italic border border-dashed border-white/8 rounded-md p-4 text-center">
                    No to-dos yet. Add your first above.
                  </div>
                ) : (
                  <ul className="space-y-1.5">
                    {todos.map((t) => (
                      <li
                        key={t.id}
                        data-testid={`todo-item-${t.id}`}
                        className={`flex items-center gap-2 rounded-md px-2 py-1.5 border transition ${
                          t.done ? "border-white/5 bg-black/25 text-white/40" : "border-white/8 bg-black/30 text-white/85"
                        }`}
                      >
                        <button
                          onClick={() => toggleTodo(t)}
                          className={`h-4 w-4 rounded flex items-center justify-center border ${
                            t.done ? "bg-emerald-500/40 border-emerald-500/50" : "border-white/25 hover:border-white/50"
                          }`}
                          aria-label="Toggle done"
                        >
                          {t.done && <Check size={11} />}
                        </button>
                        <span className={`flex-1 text-sm ${t.done ? "line-through" : ""}`}>{t.text}</span>
                        <button
                          onClick={() => removeTodo(t)}
                          className="text-white/30 hover:text-red-400"
                          aria-label="Delete"
                        ><Trash2 size={12} /></button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
